import React, { useMemo } from 'react';
import { ArrowBack, Delete, Edit, FileCopy } from '@mui/icons-material';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useParams } from 'react-router-dom';
import { ScenarioCreateInput, useAdminScenarioPlansQuery, useAdminScenarioQuery } from '../../../../../api/gql';
import { FullSizeLoader } from '../../../../../components/FullSizeLoader';
import { PageHeader } from '../../../../../components/PageHeader';
import { PageContent, PageLayout } from '../../../../../layout';
import { ModelForm } from '../../../../../modules/forms/model/ModelForm';
import { UserForm } from '../../../../../modules/forms/user/UserForm';
import { ScenarioForm } from '../../../../../modules/scenarios/ScenarioForm';

const TRANS_NS = 'universal-reminding';

export const ScenarioDetailPage = () => {
  const { t } = useTranslation(TRANS_NS, { keyPrefix: 'scenarios' });
  const { scenarioId } = useParams();

  const { data, isLoading } = useAdminScenarioQuery({ id: scenarioId ?? '' });
  const {
    data: scenarioPlansData,
    isLoading: scenarioPlansLoading,
    isError: scenarioPlansError,
  } = useAdminScenarioPlansQuery();

  const defaultValues: ScenarioCreateInput | undefined = useMemo(
    () =>
      data?.adminScenario
        ? {
            plan: data?.adminScenario.plan.name,
            subject: data?.adminScenario.subject,
            textCall: data?.adminScenario.textCall,
            textSms: data?.adminScenario.textSms,
            textEmail: data?.adminScenario.textEmail,
            name: data?.adminScenario.name,
            modelId: `${data?.adminScenario.model.name}`,
            userId: `${data?.adminScenario.user?.firstName} ${data?.adminScenario.user?.lastName}`,
          }
        : undefined,
    [data],
  );

  return (
    <PageLayout>
      <PageHeader
        title={t('detail.title')}
        backRouteTo={'/admin/universal-reminding/scenarios'}
        Icon={ArrowBack}
        action={
          <Stack direction={'row'} gap={2}>
            <Button
              startIcon={<Edit />}
              component={Link}
              to={`/admin/universal-reminding/scenarios/edit/${scenarioId}`}
            >
              {t('action.edit')}
            </Button>
            <Button
              startIcon={<FileCopy />}
              component={Link}
              to={`/admin/universal-reminding/scenarios/duplicate/${scenarioId}`}
            >
              {t('action.duplicate')}
            </Button>
            <Button
              startIcon={<Delete />}
              color={'error'}
              component={Link}
              to={`/admin/universal-reminding/scenarios/${scenarioId}/delete`}
            >
              {t('action.delete')}
            </Button>
          </Stack>
        }
      />
      <PageContent
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 4,
        }}
      >
        <Stack flex={2} gap={1}>
          <Typography variant={'h2'}>{t('detail.scenario')}</Typography>
          <Paper sx={{ width: '100%', position: 'relative', p: 2 }}>
            {scenarioPlansError ? <div>Nepodařilo se načíst upomínací plány.</div> : null}
            {isLoading || scenarioPlansLoading ? <FullSizeLoader /> : null}
            {defaultValues && scenarioPlansData ? (
              <ScenarioForm readonly defaultValues={defaultValues} scenarioPlans={scenarioPlansData.scenarioPlans} />
            ) : null}
          </Paper>
        </Stack>
        <Stack direction={'row'} gap={4} sx={{ flexWrap: 'wrap' }}>
          <Stack flex={1} gap={1}>
            <Typography variant={'h2'}>{t('detail.user')}</Typography>
            <Paper sx={{ width: '100%', position: 'relative', p: 2 }}>
              {isLoading ? <FullSizeLoader /> : null}
              {data?.adminScenario.user ? (
                <UserForm onSubmit={() => null} defaultValues={data?.adminScenario.user} readonly={true} />
              ) : null}
            </Paper>
          </Stack>
          <Stack flex={1} gap={1}>
            <Typography variant={'h2'}>{t('detail.model')}</Typography>
            <Paper sx={{ width: '100%', position: 'relative', p: 2 }}>
              {isLoading ? <FullSizeLoader /> : null}
              {data?.adminScenario.model ? (
                <ModelForm defaultValues={data?.adminScenario.model} readonly={true} />
              ) : null}
            </Paper>
          </Stack>
        </Stack>
      </PageContent>
      {/*  Outlet for modal pages */}
      <Outlet />
    </PageLayout>
  );
};
